import React, { memo } from 'react';
import {
    Table,
    Button,
    Select,
    Switch,
    Tag,
    Space
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { CustomerCategoryProductDTO, ProductDTO } from "@/shared/api/model";

const { Option } = Select;

interface ProductSelectionTabProps {
    selectedProducts: CustomerCategoryProductDTO[];
    allProducts: ProductDTO[] | undefined;
    isLoadingProducts: boolean;
    mode: 'create' | 'edit' | 'view';
    onAddProduct: (glNo: string) => void;
    onRemoveProduct: (glNo: string) => void;
    onProductPropertyChange: (glNo: string, prop: string, value: boolean) => void;
}

const ProductSelectionTab: React.FC<ProductSelectionTabProps> = memo(({
    selectedProducts,
    allProducts,
    isLoadingProducts,
    mode,
    onAddProduct,
    onRemoveProduct,
    onProductPropertyChange
}) => {
    const { dark } = useTheme();

    const availableProducts = (allProducts || []).filter(
        p => !selectedProducts.some(sp => sp.glNo === p.glNo)
    );

    const productColumns = [
        {
            title: 'GL Number',
            dataIndex: 'glNo',
            key: 'glNo'
        },
        {
            title: 'Name',
            dataIndex: 'productName',
            key: 'productName'
        },
        {
            title: 'Type',
            dataIndex: 'productTypeName',
            key: 'productTypeName',
            render: (typeName: string, record: CustomerCategoryProductDTO) => {
                const types: Record<string, { label: string, color: string }> = {
                    'S': { label: 'Savings', color: 'blue' },
                    'H': { label: 'Shares', color: 'green' },
                    'F': { label: 'Fixed Deposit', color: 'orange' },
                    'L': { label: 'Loans', color: 'red' }
                };

                const typeInfo = types[record.productType || ''] || { label: typeName || 'Unknown', color: 'default' };

                return (
                    <Tag color={typeInfo.color}>
                        {typeName || typeInfo.label}
                    </Tag>
                );
            }
        },
        {
            title: 'Required',
            key: 'isRequired',
            render: (_: any, record: CustomerCategoryProductDTO) => (
                <Switch
                    checked={record.isRequired}
                    onChange={(checked) =>
                        // @ts-ignore
                        onProductPropertyChange(record.glNo, 'isRequired', checked)
                    }
                    disabled={mode === 'view'}
                />
            )
        },
        {
            title: 'Default',
            key: 'isDefault',
            render: (_: any, record: CustomerCategoryProductDTO) => (
                <Switch
                    checked={record.isDefault}
                    onChange={(checked) =>
                        // @ts-ignore
                        onProductPropertyChange(record.glNo, 'isDefault', checked)
                    }
                    disabled={mode === 'view'}
                />
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: CustomerCategoryProductDTO) => (
                <Button
                    danger
                    icon={<DeleteOutlined />}
                    // @ts-ignore
                    onClick={() => onRemoveProduct(record.glNo)}
                    size="small"
                    disabled={mode === 'view'}
                />
            )
        }
    ];

    return (
        <div className="space-y-4">
            {mode !== 'view' && (
                <div className="mb-4">
                    <h3 className={`font-semibold mb-2 ${dark ? 'text-white' : ''}`}>
                        Add Product
                    </h3>
                    <Select
                        placeholder="Select a product to add"
                        style={{ width: '100%' }}
                        // @ts-ignore
                        onSelect={(value: string) => onAddProduct(value)}
                        optionLabelProp="label"
                        value={undefined}
                        loading={isLoadingProducts}
                        disabled={isLoadingProducts}
                    >
                        {availableProducts.map(product => (
                            <Option
                                key={product.glNo}
                                value={product.glNo}
                                label={`${product.glNo} - ${product.productName}`}
                            >
                                <div className="flex justify-between">
                                    <span>{product.productName}</span>
                                    <Tag color={
                                        product.productType === 'S' ? 'blue' :
                                            product.productType === 'H' ? 'green' :
                                                product.productType === 'F' ? 'orange' :
                                                    product.productType === 'L' ? 'red' : 'default'
                                    }>
                                        {product.productTypeName || product.productType}
                                    </Tag>
                                </div>
                            </Option>
                        ))}
                    </Select>
                </div>
            )}

            <Table
                dataSource={selectedProducts}
                columns={productColumns}
                rowKey={(record) => record.glNo || (record as any).id || Math.random().toString()}
                pagination={false}
                size="middle"
                loading={isLoadingProducts}
            />
        </div>
    );
});

ProductSelectionTab.displayName = 'ProductSelectionTab';

export default ProductSelectionTab;
